import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { api } from "../lib/api";
import Menu from "./Menu";
import type {
    Character,
    Relation,
} from "../types/characterTypes";
import {
    handleChange,
    handleChangeArray,
    handleChangeNestedArray,
    addListItem,
} from "../lib/CharacterFormLogic";

const CharacterDetail: React.FC = () => {
    const { id } = useParams();
    const [editingTile, setEditingTile] = useState<string | null>(null);
    const [character, setCharacter] = useState<Character>({} as Character);
    const [editableCharacter, setEditableCharacter] = useState<Character>({} as Character);
    const [loading, setLoading] = useState(true);

    const handleSave = async (section: keyof Character, index?: number) => {
        await axios.post(
            api.updateCharacter(editableCharacter.id!),
            editableCharacter,
            { headers: { "Content-Type": "application/json" } }
        );

        setCharacter(prev => {
            const updated = { ...prev };

            if (index !== undefined) {
                // For array-like sections (e.g., relationships)
                (updated[section] as any)[index] = (editableCharacter[section] as any)[index];
            } else {
                // For flat sections (e.g., identity)
                (updated[section] as any) = (editableCharacter[section] as any);
            }

            return updated;
        });

        setEditingTile(null);
    };

    useEffect(() => {
        const fetchCharacter = async () => {
            try {
                const res = await axios.get(api.getById("characters", id!));
                setCharacter(res.data);
                setEditableCharacter(res.data);
            } catch (error) {
                console.error("Error fetching character:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchCharacter();
    }, [id]);

    if (loading) return <div className="p-6">Loading...</div>;

    return (
        <div className="p-6 bg-[#E4F1FF] min-h-screen text-[#333]">
            <div className="flex justify-between items-start mb-6">
                <h1 className="text-4xl font-bold">{character.identity?.name}</h1>
                <Menu />
            </div>

            {character.imageUrl && (
                <div className="mb-6">
                    <img
                        src={character.imageUrl}
                        alt={character.identity?.name}
                        className="w-full max-w-sm mx-auto rounded shadow"
                    />
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Identity */}
                <Tile
                    title="Identity"
                    fields={editableCharacter.identity}
                    isEditing={editingTile === "identity"}
                    onEdit={() => setEditingTile("identity")}
                    onCancel={() => setEditingTile(null)}
                    onSave={() => handleSave("identity")}
                >
                    {editingTile === "identity" && editableCharacter.identity && (
                        <EditableFields
                            data={editableCharacter.identity}
                            onChange={(key, value) => handleChange("identity", key, value, setEditableCharacter)}
                        />
                    )}
                </Tile>

                {/* Physical Description */}
                <Tile
                    title="Physical Description"
                    fields={editableCharacter.physicalDescription}
                    isEditing={editingTile === "physicalDescription"}
                    onEdit={() => setEditingTile("physicalDescription")}
                    onCancel={() => setEditingTile(null)}
                    onSave={() => handleSave("physicalDescription")}
                >
                    {editingTile === "physicalDescription" && editableCharacter.physicalDescription && (
                        <EditableFields
                            data={editableCharacter.physicalDescription}
                            onChange={(key, value) => handleChange("physicalDescription", key, value, setEditableCharacter)}
                        />
                    )}
                </Tile>

                {/* Personality */}
                <Tile
                    title="Personality"
                    fields={editableCharacter.personality}
                    isEditing={editingTile === "personality"}
                    onEdit={() => setEditingTile("personality")}
                    onCancel={() => setEditingTile(null)}
                    onSave={() => handleSave("personality")}
                >
                    {editingTile === "personality" && editableCharacter.personality && (
                        <EditableFields
                            data={editableCharacter.personality}
                            onChange={(key, value) => handleChange("personality", key, value, setEditableCharacter)}
                        />
                    )}
                </Tile>

                {/* Backstory */}
                {editableCharacter.backstory && (
                    <Tile
                        title="Backstory"
                        isEditing={editingTile === "backstory"}
                        onEdit={() => setEditingTile("backstory")}
                        onCancel={() => setEditingTile(null)}
                        onSave={() => handleSave("backstory")}
                    >
                        {editingTile === "backstory" ? (
                            <div>
                                {/* Birthplace */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium">Birthplace</label>
                                    <input
                                        type="text"
                                        className="border rounded px-2 py-1 w-full"
                                        value={editableCharacter.backstory.birthplace || ""}
                                        onChange={(e) =>
                                            setEditableCharacter((prev) => {
                                                if (!prev) return prev;
                                                return {
                                                    ...prev,
                                                    backstory: {
                                                        ...prev.backstory,
                                                        birthplace: e.target.value,
                                                    },
                                                };
                                            })
                                        }
                                    />
                                </div>

                                {/* Key Events */}
                                <h3 className="text-lg font-medium mb-2">Key Events</h3>
                                {(editableCharacter.backstory.keyEvents || []).map((event, i) => (
                                    <div key={i} className="mb-3 border-b pb-2">
                                        {Object.entries(event).map(([key, value]) => (
                                            <div key={key} className="mb-1">
                                                <label className="block text-sm font-medium capitalize">{key}</label>
                                                <input
                                                    type="text"
                                                    className="border rounded px-2 py-1 w-full"
                                                    value={value || ""}
                                                    onChange={(e) =>
                                                        handleChangeArray("backstory", "keyEvents", i, key, e.target.value, setEditableCharacter)
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className="text-blue-600 underline"
                                    onClick={() =>
                                        addListItem(
                                            "backstory",
                                            "keyEvents",
                                            undefined,
                                            { When: "", EventDescription: "", Outcome: "" }, // <-- template object to add
                                            setEditableCharacter               // <-- your setter function
                                        )
                                    }
                                >
                                    Add Key Event
                                </button>
                            </div>
                        ) : (
                            <>
                                {editableCharacter.backstory.birthplace && (
                                    <p>
                                        <strong>Birthplace:</strong> {editableCharacter.backstory.birthplace}
                                    </p>
                                )}
                                {(editableCharacter.backstory.keyEvents || []).map((event, i) => (
                                    <div key={i} className="mt-2">
                                        <p>
                                            <strong>Description:</strong> {event.eventDescription}
                                        </p>
                                        <p>
                                            <strong>Timeline:</strong> {event.when}
                                        </p>
                                        <p>
                                            <strong>Outcome:</strong> {event.outcome}
                                        </p>
                                    </div>
                                ))}
                            </>
                        )}
                    </Tile>
                )}

                {/* Relationships */}
                {(editableCharacter.backstory?.relationships?.length
                    ? editableCharacter.backstory.relationships
                    : [{} as RelationshipGroup]  // Ensure at least one blank group appears
                ).map((group, i) => {
                    const { allies = [], enemies = [], familyMembers = [] } = group;

                    return (
                        <Tile
                            key={i}
                            title="Relationships"
                            isEditing={editingTile === `relationships-${i}`}
                            onEdit={() => setEditingTile(`relationships-${i}`)}
                            onCancel={() => setEditingTile(null)}
                            onSave={() => handleSave("backstory", i)}
                        >
                            {editingTile === `relationships-${i}` ? (
                                <div>
                                    {/* Allies */}
                                    <h3 className="text-lg font-medium mb-2">Allies</h3>
                                    {allies.map((ally, a) => (
                                        <div key={a} className="border p-2 mb-2">
                                            <label className="block text-sm font-medium mb-1">Name</label>
                                            <input
                                                type="text"
                                                className="block w-full mb-2 border rounded px-2 py-1"
                                                value={ally.name || ""}
                                                onChange={(e) =>
                                                    handleChangeNestedArray(
                                                        "backstory",
                                                        "relationships",
                                                        "allies",
                                                        i,
                                                        a,
                                                        "name",
                                                        e.target.value,
                                                        setEditableCharacter
                                                    )
                                                }
                                            />
                                            <label className="block text-sm font-medium mb-1">Relationship</label>
                                            <input
                                                type="text"
                                                className="block w-full mb-2 border rounded px-2 py-1"
                                                value={ally.relationship || ""}
                                                onChange={(e) =>
                                                    handleChangeNestedArray(
                                                        "backstory",
                                                        "relationships",
                                                        "allies",
                                                        i,
                                                        a,
                                                        "relationship",
                                                        e.target.value,
                                                        setEditableCharacter
                                                    )
                                                }
                                            />
                                            <label className="block text-sm font-medium mb-1">Description</label>
                                            <input
                                                type="text"
                                                className="block w-full border rounded px-2 py-1"
                                                value={ally.description || ""}
                                                onChange={(e) =>
                                                    handleChangeNestedArray(
                                                        "backstory",
                                                        "relationships",
                                                        "allies",
                                                        i,
                                                        a,
                                                        "description",
                                                        e.target.value,
                                                        setEditableCharacter
                                                    )
                                                }
                                            />
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        className="text-blue-600 underline"
                                        onClick={() =>
                                            addListItem("backstory", "relationships", "allies", { name: "", relationship: "", description: "" }, setEditableCharacter, i)
                                        }
                                    >
                                        Add Ally
                                    </button>

                                    {/* Enemies */}
                                    <h3 className="text-lg font-medium mb-2 mt-4">Enemies</h3>
                                    {enemies.map((enemy, eIdx) => (
                                        <div key={eIdx} className="border p-2 mb-3">
                                            <label className="block text-sm font-medium mb-1">Name</label>
                                            <input
                                                type="text"
                                                className="block w-full mb-2 border rounded px-2 py-1"
                                                value={enemy.name || ""}
                                                onChange={(e) =>
                                                    handleChangeNestedArray(
                                                        "backstory",
                                                        "relationships",
                                                        "enemies",
                                                        i,
                                                        eIdx,
                                                        "name",
                                                        e.target.value,
                                                        setEditableCharacter
                                                    )
                                                }
                                            />
                                            <label className="block text-sm font-medium mb-1">Relationship</label>
                                            <input
                                                type="text"
                                                className="block w-full mb-2 border rounded px-2 py-1"
                                                value={enemy.relationship || ""}
                                                onChange={(e) =>
                                                    handleChangeNestedArray(
                                                        "backstory",
                                                        "relationships",
                                                        "enemies",
                                                        i,
                                                        eIdx,
                                                        "relationship",
                                                        e.target.value,
                                                        setEditableCharacter
                                                    )
                                                }
                                            />
                                            <label className="block text-sm font-medium mb-1">Description</label>
                                            <input
                                                type="text"
                                                className="block w-full border rounded px-2 py-1"
                                                value={enemy.description || ""}
                                                onChange={(e) =>
                                                    handleChangeNestedArray(
                                                        "backstory",
                                                        "relationships",
                                                        "enemies",
                                                        i,
                                                        eIdx,
                                                        "description",
                                                        e.target.value,
                                                        setEditableCharacter
                                                    )
                                                }
                                            />
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        className="text-blue-600 underline"
                                        onClick={() =>
                                            addListItem("backstory", "relationships", "enemies", { name: "", relationship: "", description: "" }, setEditableCharacter, i)
                                        }
                                    >
                                        Add Enemy
                                    </button>

                                    {/* Family Members */}
                                    <h3 className="text-lg font-medium mb-2 mt-4">Family</h3>
                                    {familyMembers.map((member, fIdx) => (
                                        <div key={fIdx} className="border p-2 mb-3">
                                            <label className="block text-sm font-medium mb-1">Name</label>
                                            <input
                                                type="text"
                                                className="block w-full mb-2 border rounded px-2 py-1"
                                                value={member.name || ""}
                                                onChange={(e) =>
                                                    handleChangeNestedArray(
                                                        "backstory",
                                                        "relationships",
                                                        "familyMembers",
                                                        i,
                                                        fIdx,
                                                        "name",
                                                        e.target.value,
                                                        setEditableCharacter
                                                    )
                                                }
                                            />
                                            <label className="block text-sm font-medium mb-1">Relationship</label>
                                            <input
                                                type="text"
                                                className="block w-full mb-2 border rounded px-2 py-1"
                                                value={member.relationship || ""}
                                                onChange={(e) =>
                                                    handleChangeNestedArray(
                                                        "backstory",
                                                        "relationships",
                                                        "familyMembers",
                                                        i,
                                                        fIdx,
                                                        "relationship",
                                                        e.target.value,
                                                        setEditableCharacter
                                                    )
                                                }
                                            />
                                            <label className="block text-sm font-medium mb-1">Description</label>
                                            <input
                                                type="text"
                                                className="block w-full border rounded px-2 py-1"
                                                value={member.description || ""}
                                                onChange={(e) =>
                                                    handleChangeNestedArray(
                                                        "backstory",
                                                        "relationships",
                                                        "familyMembers",
                                                        i,
                                                        fIdx,
                                                        "description",
                                                        e.target.value,
                                                        setEditableCharacter
                                                    )
                                                }
                                            />
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        className="text-blue-600 underline"
                                        onClick={() =>
                                            addListItem("backstory", "relationships", "familyMembers", { name: "", relationship: "", description: "" }, setEditableCharacter, i)
                                        }
                                    >
                                        Add Family Member
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <RelationList title="Allies" relations={allies} />
                                    <RelationList title="Enemies" relations={enemies} />
                                    <RelationList title="Family" relations={familyMembers} />
                                </>
                            )}
                        </Tile>
                    );
                })}

                {/* Goals */}
                {editableCharacter.goals && (
                    <Tile
                        title="Goals"
                        isEditing={editingTile === "goals"}
                        onEdit={() => setEditingTile("goals")}
                        onCancel={() => setEditingTile(null)}
                        onSave={() => handleSave("goals")}
                    >
                        {editingTile === "goals" ? (
                            <div>
                                {/* Wants */}
                                <h3 className="text-lg font-medium mb-2">Wants</h3>
                                {(editableCharacter.goals.wants || []).map((w, i) => (
                                    <div key={i} className="mb-3 border-b pb-2">
                                        <label className="block text-sm font-medium">Description</label>
                                        <input
                                            type="text"
                                            className="border rounded px-2 py-1 w-full mb-1"
                                            value={w.description || ""}
                                            onChange={(e) =>
                                                handleChangeArray("goals", "wants", i, "description", e.target.value, setEditableCharacter)
                                            }
                                        />
                                        <label className="block text-sm font-medium">Timeline</label>
                                        <input
                                            type="text"
                                            className="border rounded px-2 py-1 w-full"
                                            value={w.timeline || ""}
                                            onChange={(e) =>
                                                handleChangeArray("goals", "wants", i, "timeline", e.target.value, setEditableCharacter)
                                            }
                                        />
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className="text-blue-600 underline"
                                    onClick={() =>
                                        addListItem(
                                            "goals",
                                            "wants",
                                            undefined,                         // <-- no maybeTemplate
                                            { description: "", timeline: "" }, // <-- template object to add
                                            setEditableCharacter               // <-- your setter function
                                        )
                                    }
                                >
                                    Add Want
                                </button>

                                {/* Obstacles */}
                                <h3 className="text-lg font-medium mb-2">Obstacles</h3>
                                {(editableCharacter.goals.obstacles || []).map((o, i) => (
                                    <div key={i} className="mb-3 border-b pb-2">
                                        <label className="block text-sm font-medium">Description</label>
                                        <input
                                            type="text"
                                            className="border rounded px-2 py-1 w-full"
                                            value={o.description || ""}
                                            onChange={(e) =>
                                                handleChangeArray("goals", "obstacles", i, "description", e.target.value, setEditableCharacter)
                                            }
                                        />
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className="text-blue-600 underline"
                                    onClick={() =>
                                        addListItem(
                                            "goals",
                                            "obstacles",
                                            undefined,
                                            { description: "" }, // <-- template object to add
                                            setEditableCharacter               // <-- your setter function
                                        )
                                    }
                                >
                                    Add Obstacle
                                </button>

                                {/* Internal Struggles */}
                                <h3 className="text-lg font-medium mb-2">Internal Struggles</h3>
                                {(editableCharacter.goals.internalStruggles || []).map((iS, i) => (
                                    <div key={i} className="mb-3 border-b pb-2">
                                        <label className="block text-sm font-medium">Description</label>
                                        <input
                                            type="text"
                                            className="border rounded px-2 py-1 w-full"
                                            value={iS.description || ""}
                                            onChange={(e) =>
                                                handleChangeArray("goals", "internalStruggles", i, "description", e.target.value, setEditableCharacter)
                                            }
                                        />
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className="text-blue-600 underline"
                                    onClick={() =>
                                        addListItem(
                                            "goals",
                                            "internalStruggles",
                                            undefined,
                                            { description: "" }, // <-- template object to add
                                            setEditableCharacter               // <-- your setter function
                                        )
                                    }
                                >
                                    Add Internal Struggle
                                </button>

                                {/* External Challenges */}
                                <h3 className="text-lg font-medium mb-2">External Challenges</h3>
                                {(editableCharacter.goals.externalChallenges || []).map((eC, i) => (
                                    <div key={i} className="mb-3 border-b pb-2">
                                        <label className="block text-sm font-medium">Description</label>
                                        <input
                                            type="text"
                                            className="border rounded px-2 py-1 w-full"
                                            value={eC.description || ""}
                                            onChange={(e) =>
                                                handleChangeArray("goals", "externalChallenges", i, "description", e.target.value, setEditableCharacter)
                                            }
                                        />
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className="text-blue-600 underline"
                                    onClick={() =>
                                        addListItem(
                                            "goals",
                                            "externalChallenges",
                                            undefined,
                                            { description: "" }, // <-- template object to add
                                            setEditableCharacter               // <-- your setter function
                                        )
                                    }
                                >
                                    Add External Challenge
                                </button>
                            </div>
                        ) : (
                            <>
                                <ListSection
                                    title="Wants"
                                    items={editableCharacter.goals.wants?.map(
                                        (w) => `${w.description} (Timeline: ${w.timeline})`
                                    )}
                                />
                                <ListSection
                                    title="Obstacles"
                                    items={editableCharacter.goals.obstacles?.map((o) => o.description)}
                                />
                                <ListSection
                                    title="Internal Struggles"
                                    items={editableCharacter.goals.internalStruggles?.map((i) => i.description)}
                                />
                                <ListSection
                                    title="External Challenges"
                                    items={editableCharacter.goals.externalChallenges?.map((e) => e.description)}
                                />
                            </>
                        )}
                    </Tile>
                )}
            </div>
        </div>
    );
};

const EditableFields = ({
    data,
    onChange,
}: {
    data: Record<string, string | undefined>;
    onChange: (key: string, value: string) => void;
}) => (
    <div>
        {Object.entries(data).map(([key, value]) => (
            <div key={key} className="mb-2">
                <label className="block text-sm font-medium capitalize">{key}</label>
                <input
                    type="text"
                    className="border rounded px-2 py-1 w-full"
                    value={value || ""}
                    onChange={(e) => onChange(key, e.target.value)}
                />
            </div>
        ))}
    </div>
);

const Tile = ({
    title,
    fields,
    onEdit,
    isEditing,
    onCancel,
    onSave,
    children,
}: {
    title: string;
    fields?: Record<string, string | undefined>;
    onEdit?: () => void;
    isEditing?: boolean;
    onCancel?: () => void;
    onSave?: () => void;
    children?: React.ReactNode;
}) => {
    return (
        <div className="bg-white rounded shadow p-4 relative group">
            <div className="flex justify-between items-center mb-2">
                <h2 className="text-xl font-semibold">{title}</h2>
                {!isEditing && onEdit && (
                    <button
                        onClick={onEdit}
                        className="text-sm text-blue-600 hover:underline opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    >
                        Edit
                    </button>
                )}
            </div>

            {fields && !isEditing ? (
                // Show all fields, even if values are empty
                Object.entries(fields).map(([key, value]) => (
                    <p key={key} className="text-sm text-gray-700">
                        <strong className="capitalize">{key}:</strong>{" "}
                        {value || <span className="italic text-gray-400">Empty</span>}
                    </p>
                ))
            ) : (
                children
            )}

            {isEditing && (
                <div className="mt-4 flex gap-2">
                    <button
                        onClick={onSave}
                        className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                    >
                        Save
                    </button>
                    <button
                        onClick={onCancel}
                        className="bg-gray-300 px-3 py-1 rounded hover:bg-gray-400"
                    >
                        Cancel
                    </button>
                </div>
            )}
        </div>
    );
};

const RelationList = ({
    title,
    relations,
}: {
    title: string;
    relations?: Relation[];
}) => {
    if (!relations?.length) return null;
    return (
        <div className="mt-4">
            <h3 className="text-lg font-medium">{title}</h3>
            {relations.map((rel, i) => (
                <div key={i} className="ml-2 mb-2">
                    <p>
                        <strong>Name:</strong> {rel.name}
                    </p>
                    <p>
                        <strong>Relationship:</strong> {rel.relationship}
                    </p>
                    <p>
                        <strong>Description:</strong> {rel.description}
                    </p>
                </div>
            ))}
        </div>
    );
};

const ListSection = ({
    title,
    items,
}: {
    title: string;
    items?: (string | undefined)[];
}) => {
    if (!items?.some(Boolean)) return null;
    return (
        <div className="mt-4">
            <h3 className="text-lg font-medium">{title}</h3>
            <ul className="list-disc list-inside ml-2">
                {items.map((item, i) => (item ? <li key={i}>{item}</li> : null))}
            </ul>
        </div>
    );
};

type RelationshipGroup = {
    allies?: Relation[];
    enemies?: Relation[];
    familyMembers?: Relation[];
};

export default CharacterDetail;
